import { useState, useEffect, useRef } from 'react'
import { Download, Upload, Plus, X, GripVertical, Pencil } from 'lucide-react'
import { TextField, Checkbox } from '@anyemp/ui-kit'
import ModalPortal from '../UI/ModalPortal'
import toast from 'react-hot-toast'
import { users, talents as talentsApi } from '../../services/apiService'
import { exportTalentsToClipboard, COLUMN_LABELS } from '../../utils/exportUtils'

// Storage keys
const STORAGE_KEY = 'talentExportColumns'
const COLUMN_ORDER_KEY = 'talentExportColumnOrder'
const INCLUDE_HEADERS_KEY = 'talentExportIncludeHeaders'
const EMPTY_MARKER_KEY = 'talentExportEmptyMarker'
const CUSTOM_COLUMNS_KEY = 'talentExportCustomColumns'
const COLUMN_LABELS_KEY = 'talentExportColumnLabels'
const TEMPLATES_KEY = 'talentExportTemplates'
const ACTIVE_TEMPLATE_KEY = 'talentExportActiveTemplate'

// Default export columns configuration
const DEFAULT_EXPORT_COLUMNS = {
  id: true,
  name: true,
  email: true,
  telegram: true,
  phone: true,
  department: true,
  profession: true,
  position: true,
  secondary_professions: true,
  status: true,
  last_contact_date: true,
  link: true,
  recruiter: true,
  // Additional fields
  talent_id: false,
  talent_type: false,
  contacts: false,
  other_contacts: false,
  tools: false,
  responsibilities: false,
  languages: false,
  location: false,
  salary_expectations: false,
  salary_proposed: false,
  salary_shift: false,
  salary_rate: false,
  notes: false,
  interview_date: false,
  interview_time: false,
  start_work_date: false,
  fired_date: false,
  left_date: false,
  follow_up_date: false,
  created_at: false,
  status_updated_at: false,
  status_change_reason: false,
  public_rate: false,
  public_slug: false,
  public_short_name: false,
  public_seo_title: false,
  public_seo_description: false,
  last_company: false,
  media_portrait: false,
  media_portfolio: false,
  media_youtube: false,
  media_youtube_shorts: false,
  media_resume: false,
  media_contract: false,
  media_audio: false,
  media_video: false
}

// Default column order
const DEFAULT_COLUMN_ORDER = [
  'id', 'name', 'email', 'telegram', 'phone',
  'department', 'profession', 'position', 'secondary_professions',
  'status', 'last_contact_date', 'link', 'recruiter',
  'talent_id', 'talent_type', 'contacts', 'other_contacts', 'tools', 'responsibilities', 'languages', 'location',
  'salary_expectations', 'salary_proposed', 'salary_shift', 'salary_rate',
  'notes',
  'interview_date', 'interview_time', 'start_work_date', 'fired_date', 'left_date', 'follow_up_date',
  'created_at', 'status_updated_at', 'status_change_reason',
  'public_rate', 'public_slug', 'public_short_name', 'public_seo_title', 'public_seo_description',
  'last_company',
  'media_portrait', 'media_portfolio', 'media_youtube', 'media_youtube_shorts',
  'media_resume', 'media_contract', 'media_audio', 'media_video'
]

const TalentExport = ({ talents, talentType }) => {
  const [showExportModal, setShowExportModal] = useState(false)
  const [userOptions, setUserOptions] = useState([])

  // Load export columns from localStorage or use defaults
  const loadExportColumns = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        return { ...DEFAULT_EXPORT_COLUMNS, ...parsed }
      }
    } catch (err) {
      console.error('Error loading export columns from localStorage:', err)
    }
    return DEFAULT_EXPORT_COLUMNS
  }

  // Load column order — merges saved with defaults, appends missing keys
  const loadColumnOrder = () => {
    try {
      const saved = localStorage.getItem(COLUMN_ORDER_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        const missing = DEFAULT_COLUMN_ORDER.filter(k => !parsed.includes(k))
        return missing.length > 0 ? [...parsed, ...missing] : parsed
      }
    } catch (err) {
      console.error('Error loading column order from localStorage:', err)
    }
    return DEFAULT_COLUMN_ORDER
  }

  const loadIncludeHeaders = () => {
    try {
      const saved = localStorage.getItem(INCLUDE_HEADERS_KEY)
      return saved !== null ? JSON.parse(saved) : true
    } catch {
      return true
    }
  }

  const loadEmptyMarker = () => {
    try {
      const saved = localStorage.getItem(EMPTY_MARKER_KEY)
      return saved !== null ? saved : ''
    } catch {
      return ''
    }
  }

  // Load custom columns — backward compat: ensure each has a value field
  const loadCustomColumns = () => {
    try {
      const saved = localStorage.getItem(CUSTOM_COLUMNS_KEY)
      if (!saved) return []
      const parsed = JSON.parse(saved)
      return parsed.map(c => ({ value: '', ...c }))
    } catch (err) {
      console.error('Error loading custom columns from localStorage:', err)
      return []
    }
  }

  const loadColumnLabelOverrides = () => {
    try {
      const saved = localStorage.getItem(COLUMN_LABELS_KEY)
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  }

  const loadTemplates = () => {
    try {
      const saved = localStorage.getItem(TEMPLATES_KEY)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  }

  const loadActiveTemplateId = () => {
    try {
      return localStorage.getItem(ACTIVE_TEMPLATE_KEY) || null
    } catch {
      return null
    }
  }

  const saveExportColumns = (columns) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columns))
    } catch (err) {
      console.error('Error saving export columns to localStorage:', err)
    }
  }

  const importTemplatesRef = useRef(null)
  const isFirstRender = useRef(true)

  const [exportColumns, setExportColumns] = useState(loadExportColumns)
  const [columnOrder, setColumnOrder] = useState(loadColumnOrder)
  const [includeHeaders, setIncludeHeaders] = useState(loadIncludeHeaders)
  const [emptyMarker, setEmptyMarker] = useState(loadEmptyMarker)
  const [customColumns, setCustomColumns] = useState(loadCustomColumns)
  const [columnLabelOverrides, setColumnLabelOverrides] = useState(loadColumnLabelOverrides)
  const [newColumnName, setNewColumnName] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  // Templates
  const [templates, setTemplates] = useState(loadTemplates)
  const [activeTemplateId, setActiveTemplateId] = useState(loadActiveTemplateId)
  const [exportTemplateIds, setExportTemplateIds] = useState(new Set())
  const [newTemplateName, setNewTemplateName] = useState('')
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(null)
  const [editingTemplateId, setEditingTemplateId] = useState(null)
  const [editingTemplateName, setEditingTemplateName] = useState('')
  // Label editing
  const [editingLabelKey, setEditingLabelKey] = useState(null)
  const [editingLabelValue, setEditingLabelValue] = useState('')
  // Delete confirmation
  const [confirmDeleteKey, setConfirmDeleteKey] = useState(null)
  // Drag and drop
  const [dragSrcIndex, setDragSrcIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)

  // Load user options
  useEffect(() => {
    const fetchUserOptions = async () => {
      try {
        const userResponse = await users.getAllUsers()
        if (userResponse && userResponse.success) {
          const usersData = Array.isArray(userResponse.data)
            ? userResponse.data
            : userResponse.data.users || []
          setUserOptions(usersData)
        }
      } catch (err) {
        console.error('Error loading users:', err)
      }
    }
    fetchUserOptions()
  }, [])

  // Close modal on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingLabelKey) {
          cancelEditLabel()
        } else if (editingTemplateId) {
          cancelRenameTemplate()
        } else if (showExportModal) {
          setShowExportModal(false)
        }
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [showExportModal, editingLabelKey, editingTemplateId])

  // Fetch all talents in a single bulk request (avoids N×12 concurrent sub-requests at gateway)
  const fetchAllTalents = async (talentList) => {
    const ids = talentList.map(t => t.id)
    setExportProgress(10)
    const result = await talentsApi.getTalentsByIdsForExport(ids)
    setExportProgress(100)
    return Array.isArray(result) ? result : []
  }

  // Persist activeTemplateId to localStorage
  useEffect(() => {
    if (activeTemplateId) {
      localStorage.setItem(ACTIVE_TEMPLATE_KEY, activeTemplateId)
    } else {
      localStorage.removeItem(ACTIVE_TEMPLATE_KEY)
    }
  }, [activeTemplateId])

  // Clear stale activeTemplateId if the template no longer exists
  useEffect(() => {
    if (activeTemplateId && !templates.find(t => t.id === activeTemplateId)) {
      setActiveTemplateId(null)
    }
  }, [templates])

  // Auto-update active template when settings change
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (!activeTemplateId) return

    setTemplates(prev => {
      const template = prev.find(t => t.id === activeTemplateId)
      if (!template) return prev
      const updated = prev.map(t => t.id === activeTemplateId ? {
        ...t,
        exportColumns: { ...exportColumns },
        columnOrder: [...columnOrder],
        columnLabelOverrides: { ...columnLabelOverrides },
        customColumns: [...customColumns],
        includeHeaders,
        emptyMarker
      } : t)
      localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated))
      return updated
    })
  }, [activeTemplateId, exportColumns, columnOrder, columnLabelOverrides, customColumns, includeHeaders, emptyMarker])

  // Export handler
  const handleExportToExcel = async () => {
    if (!talents || !Array.isArray(talents) || talents.length === 0) {
      toast.error('No talents to export', { duration: 2000, position: 'bottom-right' })
      return
    }
    const hasSelectedColumns = Object.values(exportColumns).some(v => v)
    if (!hasSelectedColumns) {
      toast.error('Please select at least one column to export', { duration: 2000, position: 'bottom-right' })
      return
    }

    setIsExporting(true)
    setExportProgress(0)

    try {
      const fullTalents = await fetchAllTalents(talents)
      const result = await exportTalentsToClipboard(
        fullTalents,
        exportColumns,
        userOptions,
        {
          columnOrder,
          includeHeaders,
          emptyMarker,
          customLabels: getCustomLabelsMap(),
          customValues: getCustomValuesMap()
        }
      )

      toast.success(`Exported ${result.count} ${talentType}(s) to clipboard. Paste into Excel!`, {
        duration: 3000,
        position: 'bottom-right',
      })
      setShowExportModal(false)
    } catch (err) {
      console.error('Failed to export data:', err)
      toast.error('Failed to export data to clipboard', { duration: 2000, position: 'bottom-right' })
    } finally {
      setIsExporting(false)
      setExportProgress(0)
    }
  }

  // Column selection
  const toggleExportColumn = (columnKey) => {
    setExportColumns(prev => {
      const updated = { ...prev, [columnKey]: !prev[columnKey] }
      saveExportColumns(updated)
      return updated
    })
  }

  const selectAllExportColumns = () => {
    const allColumns = Object.keys(DEFAULT_EXPORT_COLUMNS).reduce((acc, key) => {
      acc[key] = true
      return acc
    }, {})
    setExportColumns(allColumns)
    saveExportColumns(allColumns)
  }

  const deselectAllExportColumns = () => {
    const noColumns = Object.keys(DEFAULT_EXPORT_COLUMNS).reduce((acc, key) => {
      acc[key] = false
      return acc
    }, {})
    setExportColumns(noColumns)
    saveExportColumns(noColumns)
  }

  const resetExportColumns = () => {
    setExportColumns(DEFAULT_EXPORT_COLUMNS)
    saveExportColumns(DEFAULT_EXPORT_COLUMNS)
    setColumnOrder(DEFAULT_COLUMN_ORDER)
    localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(DEFAULT_COLUMN_ORDER))
    setCustomColumns([])
    localStorage.setItem(CUSTOM_COLUMNS_KEY, JSON.stringify([]))
    setColumnLabelOverrides({})
    localStorage.setItem(COLUMN_LABELS_KEY, JSON.stringify({}))
  }

  // Templates
  const applySettings = (settings) => {
    const cols = settings.exportColumns || DEFAULT_EXPORT_COLUMNS
    setExportColumns(cols)
    saveExportColumns(cols)

    const order = settings.columnOrder || DEFAULT_COLUMN_ORDER
    setColumnOrder(order)
    localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(order))

    const overrides = settings.columnLabelOverrides || {}
    setColumnLabelOverrides(overrides)
    localStorage.setItem(COLUMN_LABELS_KEY, JSON.stringify(overrides))

    const custom = (settings.customColumns || []).map(c => ({ value: '', ...c }))
    setCustomColumns(custom)
    localStorage.setItem(CUSTOM_COLUMNS_KEY, JSON.stringify(custom))

    const headers = settings.includeHeaders !== undefined ? settings.includeHeaders : true
    setIncludeHeaders(headers)
    localStorage.setItem(INCLUDE_HEADERS_KEY, JSON.stringify(headers))

    const marker = settings.emptyMarker || ''
    setEmptyMarker(marker)
    localStorage.setItem(EMPTY_MARKER_KEY, marker)
  }

  const saveTemplate = () => {
    const name = newTemplateName.trim()
    if (!name) return

    const id = `tpl_${Date.now()}`
    const template = {
      id,
      name,
      exportColumns: { ...DEFAULT_EXPORT_COLUMNS },
      columnOrder: [...DEFAULT_COLUMN_ORDER],
      columnLabelOverrides: {},
      customColumns: [],
      includeHeaders: true,
      emptyMarker: ''
    }

    const updated = [...templates, template]
    setTemplates(updated)
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated))
    applySettings(template)
    setActiveTemplateId(id)
    setNewTemplateName('')
    setShowSaveTemplate(false)
    toast.success(`Template "${name}" created`, { duration: 2000, position: 'bottom-right' })
  }

  const applyTemplate = (tpl) => {
    applySettings(tpl)
    setActiveTemplateId(tpl.id)
    toast.success(`Template "${tpl.name}" applied`, { duration: 2000, position: 'bottom-right' })
  }

  const deleteTemplate = (id) => {
    const updated = templates.filter(t => t.id !== id)
    setTemplates(updated)
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated))
    if (activeTemplateId === id) setActiveTemplateId(null)
    setConfirmDeleteTemplate(null)
  }

  const renameTemplate = (id) => {
    const name = editingTemplateName.trim()
    if (!name) return
    const updated = templates.map(t => t.id === id ? { ...t, name } : t)
    setTemplates(updated)
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated))
    setEditingTemplateId(null)
    setEditingTemplateName('')
  }

  const cancelRenameTemplate = () => {
    setEditingTemplateId(null)
    setEditingTemplateName('')
  }

  const toggleExportTemplateId = (id) => {
    setExportTemplateIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const exportTemplatesFile = () => {
    if (templates.length === 0) {
      toast.error('No templates to export', { duration: 2000, position: 'bottom-right' })
      return
    }
    const toExport = exportTemplateIds.size > 0
      ? templates.filter(t => exportTemplateIds.has(t.id))
      : templates
    const json = JSON.stringify(toExport, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'talent-export-templates.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportTemplatesFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result)
        if (!Array.isArray(parsed)) {
          toast.error('Invalid template file', { duration: 2000, position: 'bottom-right' })
          return
        }
        const valid = parsed
          .filter(t => t.name && t.exportColumns && t.columnOrder)
          .map(t => ({ ...t, id: `tpl_${Date.now()}_${Math.random().toString(36).slice(2)}` }))

        if (valid.length === 0) {
          toast.error('No valid templates found in file', { duration: 2000, position: 'bottom-right' })
          return
        }
        const updated = [...templates, ...valid]
        setTemplates(updated)
        localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated))
        toast.success(`Imported ${valid.length} template(s)`, { duration: 2000, position: 'bottom-right' })
      } catch {
        toast.error('Failed to parse template file', { duration: 2000, position: 'bottom-right' })
      }
      e.target.value = ''
    }
    reader.readAsText(file)
  }

  // Custom columns
  const addCustomColumn = () => {
    const label = newColumnName.trim()
    if (!label) return

    const key = `custom_${Date.now()}`
    const newCustom = [...customColumns, { key, label, value: '' }]
    setCustomColumns(newCustom)
    localStorage.setItem(CUSTOM_COLUMNS_KEY, JSON.stringify(newCustom))

    setColumnOrder(prev => {
      const updated = [...prev, key]
      localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(updated))
      return updated
    })
    setExportColumns(prev => {
      const updated = { ...prev, [key]: true }
      saveExportColumns(updated)
      return updated
    })

    setNewColumnName('')
  }

  const removeCustomColumn = (key) => {
    const newCustom = customColumns.filter(c => c.key !== key)
    setCustomColumns(newCustom)
    localStorage.setItem(CUSTOM_COLUMNS_KEY, JSON.stringify(newCustom))

    setColumnOrder(prev => {
      const updated = prev.filter(k => k !== key)
      localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(updated))
      return updated
    })
    setExportColumns(prev => {
      const updated = { ...prev }
      delete updated[key]
      saveExportColumns(updated)
      return updated
    })

    // Also remove any label override
    const updatedOverrides = { ...columnLabelOverrides }
    delete updatedOverrides[key]
    setColumnLabelOverrides(updatedOverrides)
    localStorage.setItem(COLUMN_LABELS_KEY, JSON.stringify(updatedOverrides))
  }

  const updateCustomColumnValue = (key, value) => {
    const updated = customColumns.map(c => c.key === key ? { ...c, value } : c)
    setCustomColumns(updated)
    localStorage.setItem(CUSTOM_COLUMNS_KEY, JSON.stringify(updated))
  }

  const isCustomColumn = (key) => customColumns.some(c => c.key === key)

  // Label management
  const getColumnDisplayLabel = (key) => {
    if (columnLabelOverrides[key]) return columnLabelOverrides[key]
    if (COLUMN_LABELS[key]) return COLUMN_LABELS[key]
    const custom = customColumns.find(c => c.key === key)
    return custom?.label || key
  }

  const startEditLabel = (key) => {
    setEditingLabelKey(key)
    setEditingLabelValue(getColumnDisplayLabel(key))
  }

  const commitEditLabel = () => {
    if (!editingLabelKey) return
    const newLabel = editingLabelValue.trim()
    const defaultLabel = COLUMN_LABELS[editingLabelKey] ||
      customColumns.find(c => c.key === editingLabelKey)?.label ||
      editingLabelKey

    const updated = { ...columnLabelOverrides }
    if (!newLabel || newLabel === defaultLabel) {
      delete updated[editingLabelKey]
    } else {
      updated[editingLabelKey] = newLabel
    }

    setColumnLabelOverrides(updated)
    localStorage.setItem(COLUMN_LABELS_KEY, JSON.stringify(updated))
    setEditingLabelKey(null)
    setEditingLabelValue('')
  }

  const cancelEditLabel = () => {
    setEditingLabelKey(null)
    setEditingLabelValue('')
  }

  // Export data helpers
  const getCustomLabelsMap = () => {
    const map = {}
    // Label overrides take priority (both standard and custom)
    Object.entries(columnLabelOverrides).forEach(([k, v]) => { if (v) map[k] = v })
    // Custom column default labels as fallback
    customColumns.forEach(c => { if (!map[c.key]) map[c.key] = c.label })
    return map
  }

  const getCustomValuesMap = () => {
    return customColumns.reduce((acc, c) => {
      acc[c.key] = c.value || ''
      return acc
    }, {})
  }

  // Drag and drop
  const handleDragStart = (e, index) => {
    setDragSrcIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(index))
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverIndex !== index) setDragOverIndex(index)
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    if (dragSrcIndex === null || dragSrcIndex === dropIndex) {
      setDragSrcIndex(null)
      setDragOverIndex(null)
      return
    }

    setColumnOrder(prev => {
      const newOrder = [...prev]
      const [dragged] = newOrder.splice(dragSrcIndex, 1)
      newOrder.splice(dropIndex, 0, dragged)
      localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(newOrder))
      return newOrder
    })

    setDragSrcIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDragSrcIndex(null)
    setDragOverIndex(null)
  }

  return (
    <>
      {/* Export Button */}
      <button
        onClick={() => setShowExportModal(true)}
        className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
      >
        <Download className="w-4 h-4" />
        <span>Export to Excel</span>
      </button>

      {/* Modal */}
      {showExportModal && (
        <ModalPortal onClose={() => setShowExportModal(false)} ariaLabel="Export talent">
          <div className="flex max-h-[calc(100dvh-1.5rem)] w-full min-w-0 max-w-2xl flex-col rounded-lg border border-light-border-primary bg-light-bg-primary shadow-xl dark:border-dark-border-primary dark:bg-dark-bg-primary sm:max-h-[calc(100dvh-2rem)]">

            {/* Header */}
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-light-border-primary px-4 py-4 dark:border-dark-border-primary sm:px-6">
              <h2 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">
                Export Settings
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="min-w-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">

              {/* Templates */}
              <div className="mb-4 pb-4 border-b border-light-border-primary dark:border-dark-border-primary">
                <div className="mb-2 flex min-w-0 flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                    Templates
                  </h3>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => importTemplatesRef.current?.click()}
                      className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      <Upload className="w-3 h-3" />
                      <span>Import</span>
                    </button>
                    <button
                      onClick={exportTemplatesFile}
                      disabled={templates.length === 0}
                      title={exportTemplateIds.size > 0 ? `Export ${exportTemplateIds.size} selected` : 'Export all'}
                      className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      <span>{exportTemplateIds.size > 0 ? `Export (${exportTemplateIds.size})` : 'Export all'}</span>
                    </button>
                  </div>
                </div>
                <input ref={importTemplatesRef} type="file" accept=".json" className="hidden" onChange={handleImportTemplatesFile} />

                {/* Template list */}
                <div className="space-y-0.5">
                  {templates.length === 0 && !showSaveTemplate && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic py-1">No saved templates</p>
                  )}
                  {templates.map(tpl => {
                    const isActive = activeTemplateId === tpl.id
                    const isRenaming = editingTemplateId === tpl.id
                    return (
                      <div
                        key={tpl.id}
                        className={[
                          'group flex min-w-0 flex-wrap items-center gap-2 px-2 py-1.5 rounded-md transition-colors',
                          isActive ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800',
                        ].join(' ')}
                      >
                        {/* Checkbox for selective JSON export */}
                        <Checkbox
                          checked={exportTemplateIds.has(tpl.id)}
                          onChange={() => toggleExportTemplateId(tpl.id)}
                          size="small"
                          color="primary"
                          sx="flex-shrink-0"
                        />

                        {/* Active dot */}
                        <span className={['w-1.5 h-1.5 rounded-full flex-shrink-0', isActive ? 'bg-blue-500' : 'bg-transparent'].join(' ')} />

                        {/* Name / rename input */}
                        {isRenaming ? (
                          <input
                            type="text"
                            value={editingTemplateName}
                            autoFocus
                            maxLength={40}
                            onChange={(e) => setEditingTemplateName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameTemplate(tpl.id)
                              if (e.key === 'Escape') cancelRenameTemplate()
                            }}
                            className="min-w-0 flex-1 text-sm bg-transparent border-b border-blue-500 dark:border-blue-400 focus:outline-none text-light-text-primary dark:text-dark-text-primary"
                          />
                        ) : (
                          <button
                            onClick={() => applyTemplate(tpl)}
                            title="Apply template"
                            className={[
                              'min-w-0 flex-1 text-left text-sm truncate transition-colors',
                              isActive
                                ? 'text-blue-700 dark:text-blue-300 font-medium'
                                : 'text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary',
                            ].join(' ')}
                          >
                            {tpl.name}
                          </button>
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap items-center gap-1 sm:ml-auto">
                          {isRenaming ? (
                            <>
                              <button onMouseDown={(e) => e.preventDefault()} onClick={() => renameTemplate(tpl.id)} disabled={!editingTemplateName.trim()} className="text-xs text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-30 px-1.5 py-0.5 transition-colors">Save</button>
                              <button onMouseDown={(e) => e.preventDefault()} onClick={cancelRenameTemplate} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-1.5 py-0.5 transition-colors">Cancel</button>
                            </>
                          ) : (
                            <>
                              {!isActive && (
                                <button onClick={() => applyTemplate(tpl)} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">Apply</button>
                              )}
                              {isActive && (
                                <span className="text-xs text-blue-500 dark:text-blue-400 px-1.5 py-0.5">Active</span>
                              )}
                              <button onClick={() => { setEditingTemplateId(tpl.id); setEditingTemplateName(tpl.name) }} className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Rename">
                                <Pencil className="w-3 h-3" />
                              </button>
                              {confirmDeleteTemplate === tpl.id ? (
                                <>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Delete?</span>
                                  <button onClick={() => deleteTemplate(tpl.id)} className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 px-1 transition-colors">Yes</button>
                                  <button onClick={() => setConfirmDeleteTemplate(null)} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-1 transition-colors">No</button>
                                </>
                              ) : (
                                <button onClick={() => setConfirmDeleteTemplate(tpl.id)} className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })}

                  {/* New template row */}
                  {showSaveTemplate ? (
                    <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-md bg-blue-50 px-2 py-1.5 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:ring-blue-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-transparent flex-shrink-0" />
                      <input
                        type="text"
                        value={newTemplateName}
                        placeholder="Template name…"
                        autoFocus
                        maxLength={40}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveTemplate()
                          if (e.key === 'Escape') { setShowSaveTemplate(false); setNewTemplateName('') }
                        }}
                        className="flex-1 text-sm bg-transparent border-b border-blue-500 dark:border-blue-400 focus:outline-none text-light-text-primary dark:text-dark-text-primary placeholder-gray-400"
                      />
                      <button onMouseDown={(e) => e.preventDefault()} onClick={saveTemplate} disabled={!newTemplateName.trim()} className="text-xs text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-30 px-1.5 py-0.5 transition-colors">Save</button>
                      <button onMouseDown={(e) => e.preventDefault()} onClick={() => { setShowSaveTemplate(false); setNewTemplateName('') }} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-1.5 py-0.5 transition-colors">Cancel</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setShowSaveTemplate(true); setNewTemplateName('') }}
                      className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md w-full transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      New template
                    </button>
                  )}
                </div>
              </div>

              {/* Columns Header */}
              <div className="mb-3 flex min-w-0 flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                  Select columns ({Object.values(exportColumns).filter(Boolean).length} selected)
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button onClick={selectAllExportColumns} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors">All</button>
                  <button onClick={deselectAllExportColumns} className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors">None</button>
                  <button onClick={resetExportColumns} className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors">Reset</button>
                </div>
              </div>

              {/* Columns List */}
              <div className="mb-4" onDragLeave={() => setDragOverIndex(null)}>
                {columnOrder.map((key, index) => {
                  const isCustom = isCustomColumn(key)
                  const isEditing = editingLabelKey === key
                  const isDragging = dragSrcIndex === index
                  const isDragOver = dragOverIndex === index && dragSrcIndex !== null && dragSrcIndex !== index
                  const hasOverride = !!columnLabelOverrides[key]

                  return (
                    <div key={key}>
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={[
                          'group flex min-w-0 flex-wrap items-center gap-2 py-1.5 px-2 rounded transition-all',
                          'hover:bg-gray-50 dark:hover:bg-gray-800',
                          isDragging ? 'opacity-40' : '',
                          isDragOver ? 'border-t-2 border-blue-500 dark:border-blue-400' : 'border-t-2 border-transparent',
                        ].join(' ')}
                      >
                        <GripVertical className="w-4 h-4 text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0" />
                        <Checkbox
                          checked={!!exportColumns[key]}
                          onChange={() => toggleExportColumn(key)}
                          size="small"
                          color="success"
                          sx="flex-shrink-0"
                        />
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingLabelValue}
                            autoFocus
                            maxLength={40}
                            onChange={(e) => setEditingLabelValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEditLabel()
                              if (e.key === 'Escape') cancelEditLabel()
                            }}
                            className="min-w-0 flex-1 text-sm bg-transparent border-b border-blue-500 dark:border-blue-400 focus:outline-none text-light-text-primary dark:text-dark-text-primary"
                          />
                        ) : (
                          <span className={[
                            'min-w-0 flex-1 text-sm truncate',
                            isCustom ? 'text-blue-600 dark:text-blue-400' : 'text-light-text-secondary dark:text-dark-text-secondary',
                            hasOverride ? 'font-medium' : '',
                          ].join(' ')}>
                            {getColumnDisplayLabel(key)}
                          </span>
                        )}
                        <div className="flex flex-wrap items-center gap-1 sm:ml-auto">
                          {isEditing ? (
                            <>
                              <button onMouseDown={(e) => e.preventDefault()} onClick={commitEditLabel} className="text-xs text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 px-1.5 py-0.5 transition-colors">Save</button>
                              <button onMouseDown={(e) => e.preventDefault()} onClick={cancelEditLabel} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-1.5 py-0.5 transition-colors">Cancel</button>
                            </>
                          ) : (
                            <button onClick={() => startEditLabel(key)} className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Rename column">
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                          {isCustom && !isEditing && confirmDeleteKey !== key && (
                            <button onClick={() => setConfirmDeleteKey(key)} className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove column">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {isCustom && confirmDeleteKey === key && (
                            <>
                              <span className="text-xs text-gray-500 dark:text-gray-400">Remove?</span>
                              <button onClick={() => { removeCustomColumn(key); setConfirmDeleteKey(null) }} className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 px-1.5 py-0.5 transition-colors">Yes</button>
                              <button onClick={() => setConfirmDeleteKey(null)} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-1.5 py-0.5 transition-colors">No</button>
                            </>
                          )}
                        </div>
                      </div>
                      {isCustom && isEditing && (
                        <div className="mb-1 ml-0 flex min-w-0 flex-wrap items-center gap-2 px-2 sm:ml-10">
                          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">Value:</span>
                          <input
                            type="text"
                            value={customColumns.find(c => c.key === key)?.value || ''}
                            placeholder="Static value for all rows…"
                            onChange={(e) => updateCustomColumnValue(key, e.target.value)}
                            className="min-w-0 flex-1 px-2 py-0.5 text-xs border rounded border-light-border-primary dark:border-dark-border-primary bg-transparent text-light-text-secondary dark:text-dark-text-secondary focus:outline-none focus:ring-1 focus:ring-blue-400 dark:focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Add Custom Column */}
              <div className="mb-6 flex min-w-0 flex-wrap items-center gap-2">
                <TextField
                  type="text"
                  size="small"
                  fullWidth
                  value={newColumnName}
                  placeholder="New column name…"
                  onChange={(e) => setNewColumnName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCustomColumn()}
                  maxLength={30}
                  sx={{ root: 'flex-1' }}
                />
                <button
                  onClick={addCustomColumn}
                  disabled={!newColumnName.trim()}
                  className="flex min-h-10 items-center gap-1 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add column</span>
                </button>
              </div>

              {/* Options */}
              <div className="border-t border-light-border-primary dark:border-dark-border-primary pt-4 space-y-3">
                <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">Options</h3>
                <Checkbox
                  label="Include column headers"
                  size="small"
                  color="success"
                  checked={includeHeaders}
                  onChange={(checked) => {
                    setIncludeHeaders(checked)
                    localStorage.setItem(INCLUDE_HEADERS_KEY, JSON.stringify(checked))
                  }}
                />
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <label className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Mark empty cells with:</label>
                  <TextField
                    type="text"
                    size="small"
                    value={emptyMarker}
                    placeholder="(blank)"
                    onChange={(e) => {
                      setEmptyMarker(e.target.value)
                      localStorage.setItem(EMPTY_MARKER_KEY, e.target.value)
                    }}
                    maxLength={5}
                    sx={{ root: 'w-24' }}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap justify-end gap-3 border-t border-light-border-primary px-4 py-4 dark:border-dark-border-primary sm:px-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="min-h-11 flex-1 px-4 py-2 text-sm text-gray-600 transition-colors hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 sm:flex-none"
              >
                Cancel
              </button>
              <button
                onClick={handleExportToExcel}
                disabled={isExporting || !talents || talents.length === 0 || !Object.values(exportColumns).some(v => v)}
                className="min-h-11 min-w-0 flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-green-500 dark:hover:bg-green-600 sm:min-w-[160px] sm:flex-none"
              >
                {isExporting ? `Loading... ${exportProgress}%` : `Export (${talents?.length || 0} records)`}
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  )
}

export default TalentExport
